/**
 * « Journal des avoirs fournisseur » — /avoirs and /avoirs/:id (design/ux/03_avoirs_bc_reclamations.md
 * §2, §6.2; refonte WP-30). Every sentence of the journal, its drawer, the règlement and cancellation
 * dialogs and the « Avoirs émis par vos fournisseurs connectés » tab, verbatim from the design where it
 * gives one (§0.6, §2.4-§2.8), once. Vouvoiement, sentence case, no raw enum value, no English.
 */
import { AVOIR_BORNES, ETAT_AVOIR, TYPE_AVOIR } from "@pharmakon/shared/constants/avoirs.js";

const pluriel = (n, un, plusieurs) => (n > 1 ? plusieurs : un);

export const M_AJ = Object.freeze({
  titre: "Journal des avoirs fournisseur",
  sousTitre: "Avoirs d'écart, avoirs produits et ristournes — état, règlement et rapprochement avec vos fournisseurs",
  recherche: "Rechercher : N° avoir, réf. fournisseur, N° facture …",
  rechercheNotes: "Rechercher : N° avoir fournisseur, N° e-facture …",
  grille: "Avoirs fournisseur",
  nouvelAvoir: "Nouvel avoir",
  filtres: Object.freeze({
    fournisseur: "Fournisseur",
    periode: "Période",
    type: "Type",
    etat: "État",
    rapprochement: "Rapprochement",
    /** ux/03 §2.3 « Plus de filtres » — the avoirs crediting a bon de retour (WP-31, A-57 (4)). */
    bonRetour: "Bon de retour lié",
  }),
  onglets: Object.freeze({
    pharmacie: "Avoirs de la pharmacie",
    notes: "Avoirs émis par vos fournisseurs connectés",
    aRapprocher: (n) => `${n} à rapprocher`,
  }),
  /** ux/03 §2.3 — the état chips (plural forms of the chips; the column uses the registry words). */
  etats: Object.freeze({
    tous: "Tous",
    [ETAT_AVOIR.EN_ATTENTE]: Object.freeze({ long: "En attente", court: "En attente" }),
    [ETAT_AVOIR.REGLE_PARTIELLEMENT]: Object.freeze({ long: "Réglés partiellement", court: "Réglés part." }),
    [ETAT_AVOIR.REGLE]: Object.freeze({ long: "Réglés", court: "Réglés" }),
    [ETAT_AVOIR.ANNULE]: Object.freeze({ long: "Annulés", court: "Annulés" }),
  }),
  colonnes: Object.freeze({
    numero: "N° avoir",
    date: "Date",
    fournisseur: "Fournisseur",
    type: "Type",
    reference: "Réf. fourn.",
    ht: "Total HT",
    tva: "TVA",
    ttc: "Total TTC",
    reste: "Reste",
    etat: "État",
  }),
  /** §2.1 — the footer over the whole filter. */
  pied: ({ nombre, ht, tva, ttc, regle, reste }) =>
    `${nombre} ${pluriel(nombre, "avoir", "avoirs")} · HT ${ht} · TVA ${tva} · TTC ${ttc} · Réglé ${regle} · Reste à régler ${reste} DA`,
  piedCompact: ({ nombre, ttc, regle, reste }) =>
    `${nombre} ${pluriel(nombre, "avoir", "avoirs")} · TTC ${ttc} · Réglé ${regle} · Reste ${reste} DA`,
  piedAnnules: (n) => `dont ${n} ${pluriel(n, "annulé", "annulés")}, hors montants`,
  /** §2.8 — states. */
  vide: "Aucun avoir fournisseur pour l'instant.",
  videAide: "Créez un avoir depuis une facture présentant un écart, ou avec « Nouvel avoir » (F3).",
  videFiltre: "Aucun avoir ne correspond à ces critères.",
  effacerFiltres: "Effacer les filtres",
  erreur: "Le journal n'a pas pu être chargé.",
  interdit: "Vous n'avez pas les droits pour consulter les avoirs fournisseur. Contactez le propriétaire de l'officine.",
  introuvable: "Cet avoir est introuvable dans votre officine.",
  exportEchec: "L'export CSV n'a pas pu être produit.",
});

/** ux/03 §2.5 — the detail drawer. */
export const M_TIROIR = Object.freeze({
  chargement: "Chargement de l'avoir…",
  erreur: "L'avoir n'a pas pu être chargé.",
  champs: Object.freeze({
    fournisseur: "Fournisseur",
    date: "Date de l'avoir",
    saisiPar: "Saisi par",
    reference: "Réf. fournisseur",
    motif: "Motif",
    liens: "Liens",
    noteRapprochee: "Avoir fournisseur rapproché",
    // refonte WP-83 (D-32, CP §9) — recorded vs pending: is this credit in the solde?
    cycle: "Statut comptable",
  }),
  /** Refonte WP-83 (D-32) — the « Statut comptable » line of the drawer. */
  cycle: Object.freeze({
    instance: "En instance — hors solde : crédit en attente (non déduit) jusqu'à la confirmation du fournisseur.",
    confirme: ({ date, par }) => `Confirmé le ${date}${par ? ` par ${par}` : ""} — déduit du solde du fournisseur.`,
    confirmeEnregistrement: (date) => `Confirmé à l'enregistrement${date ? ` (${date})` : ""} — déduit du solde du fournisseur.`,
    sansCredit: "Sans crédit — ses lignes « Reçu non facturé » se règlent par la facture à venir ou l'enlèvement du fournisseur.",
  }),
  /** Refonte WP-83 (A-80, A-104 (9)) — one linked avoir. */
  lien: ({ numero, type, statut, ttc }) => `${numero} · ${type} · ${statut} · ${ttc} DA`,
  lienAnnule: "Annulé",
  origine: "Issu de",
  derives: "A donné",
  sections: Object.freeze({
    factures: "Factures imputées",
    facturesRistourne: "Factures liées",
    lignes: "Lignes",
    reglements: "Règlements",
    historique: "Historique",
    // refonte WP-83 (A-80, A-104 (9)) — the avoir it comes from and those derived from it
    liens: "Avoirs liés",
    bonsRetour: "Bons de retour",
    // WP-35 (ux/03 §2.4 « Liens ») — the réclamation(s) the avoir answers
    reclamations: "Réclamations",
  }),
  facture: (libelle, interne, date) => `${libelle}${interne != null ? ` (n° ${interne})` : ""}${date ? ` · ${date}` : ""}`,
  // refonte WP-81 (CP §8.2) — a facture named without any amount on it (« Montant global » over several)
  reference: "(référence)",
  // refonte WP-81 — the allocation method of a crédit fournisseur, as chosen
  repartition: "Répartition",
  ligneManquant: (lot) => `${lot ? `Lot ${lot} · ` : ""}aucun mouvement de stock`,
  ligneRetour: (lot, u) => `${lot ? `Lot ${lot} · ` : ""}−${u} u. sorties du stock`,
  // WP-31 (OP-03-21) — the units left with the bon de retour the line credits
  ligneRetourBon: (lot, u, numero) => `${lot ? `Lot ${lot} · ` : ""}−${u} u. sorties par le bon de retour ${numero}`,
  bonRetourUnites: (u) => `${u} u.`,
  // refonte WP-83 (EXE-88; WP-84, WP-85) — a line's persisted stock decision, réclamation and « Reçu non facturé »
  lot: (lot) => `Lot ${lot}`,
  ligneUnitesSorties: (u) => `−${u} u.`,
  ligneReclamation: (numero) => `réclamation ${numero}`,
  ligneValeurEstimee: (ttc) => `valeur estimée ${ttc} DA, sans crédit`,
  natureRecuNonFacture: "Reçu non facturé",
  ligneModifiee: (calculee) => `modifiée (écart constaté ${calculee})`,
  totaux: ({ ht, tva, ttc }) => `HT ${ht} · TVA ${tva} · TTC ${ttc} DA`,
  aucunReglement: (reste) => `Aucun règlement enregistré. Reste à régler ${reste} DA.`,
  reglementFournisseur: ({ date, montant, reference, par }) =>
    `${date} · ${montant} DA · réf. ${reference ?? "—"}${par ? ` · ${par}` : ""}`,
  reglementRistourne: ({ date, montant, mode, compte, par }) =>
    `${date} · ${montant} DA · ${mode ?? "—"}${compte ? ` · ${compte}` : ""}${par ? ` · ${par}` : ""}`,
  reglementAnnule: "contre-passé",
  /** §2.4 — the meaning of the état, in the drawer. */
  sens: Object.freeze({
    [ETAT_AVOIR.EN_ATTENTE]: Object.freeze({
      fournisseur: "Compté dans le solde ; avoir du fournisseur non encore reçu.",
      ristourne: "Non encaissée.",
    }),
    // refonte WP-83 (D-32) — an avoir en instance is NOT in the solde
    instance: (ttc) =>
      `En instance : ce crédit de ${ttc} DA n'est pas déduit du solde du fournisseur. Confirmez l'avoir quand le fournisseur l'a émis ou accepté.`,
    partiel: ({ regle, reste }) => `${regle} DA reçus, ${reste} DA restent attendus.`,
    regleFournisseur: (reference) => `Avoir du fournisseur reçu (réf. ${reference ?? "—"}).`,
    regleRistourne: ({ date, mode }) => `Encaissée le ${date}${mode ? ` (${mode})` : ""}.`,
    annule: ({ date, par, motif }) => `Annulé le ${date}${par ? ` par ${par}` : ""} : ${motif ?? "—"}.`,
  }),
  actions: Object.freeze({
    // refonte WP-83 (D-32, A-104 (4))
    confirmer: "Confirmer l'avoir",
    regler: "Enregistrer le règlement",
    imprimer: "Imprimer",
    email: "Envoyer par e-mail",
    plus: "Plus d'actions",
    annuler: "Annuler l'avoir…",
    precedent: "Avoir précédent",
    suivant: "Avoir suivant",
    facture: (libelle) => `Ouvrir la facture ${libelle}`,
    bonRetour: (numero) => `Ouvrir le bon de retour ${numero}`,
    reclamation: (numero) => `Ouvrir la réclamation ${numero}`,
    avoir: (numero) => `Ouvrir l'avoir ${numero}`,
  }),
  impressionEchec: "Le document de l'avoir n'a pas pu être ouvert.",
});

/** ux/03 §2.6 — « Enregistrer le règlement » (replaces MarkAvoirPaidModal). */
export const M_REGLEMENT = Object.freeze({
  titreFournisseur: "Avoir du fournisseur reçu",
  titreRistourne: "Encaissement de la ristourne",
  sousTitre: (numero, reste) => `Avoir ${numero} · reste à régler ${reste} DA`,
  champs: Object.freeze({
    reference: "Réf. avoir fournisseur",
    date: "Date",
    montantFournisseur: "Montant de l'avoir fournisseur (TTC)",
    montantRecu: "Montant reçu",
    mode: "Mode",
    compte: "Compte de réception",
    referencePaiement: "Référence du paiement",
    observations: "Observations",
  }),
  aide: Object.freeze({
    referencePaiement: "N° de chèque ou de virement.",
    montantFournisseur: "Un règlement partiel est accepté ; il ne peut pas dépasser le reste à régler.",
    ristourneSansMontant:
      "Cet avoir a été enregistré sans montant : le montant reçu devient le montant de la ristourne.",
  }),
  // refonte WP-83 (D-32) — only a CONFIRMED avoir is offered this règlement: its credit is already in the solde
  consequenceFournisseur: "L'avoir passera à « Réglé » ; le solde fournisseur ne change pas (ce crédit confirmé y est déjà déduit).",
  consequencePartielle: (reste) =>
    `L'avoir passera à « Réglé partiellement » ; ${reste} DA resteront attendus. Le solde fournisseur ne change pas.`,
  consequenceRistourne: (montant) => `Une recette de ${montant} DA sera enregistrée.`,
  erreurs: Object.freeze({
    depasse: (reste) => `Le montant dépasse le reste à régler (${reste} DA).`,
    zero: "Saisissez un montant supérieur à 0.",
    montantInvalide: "Saisissez un montant en DA avec au plus deux décimales.",
    reference: "Saisissez la référence de l'avoir du fournisseur : « Réglé » signifie que son avoir a été reçu.",
    referenceLongue: `La référence ne peut pas dépasser ${AVOIR_BORNES.reference_max} caractères.`,
    compte: "Choisissez le compte qui a reçu le remboursement.",
    aucunCompte: "Aucun compte de trésorerie actif. Créez-en un depuis la trésorerie avant d'encaisser.",
    mode: "Choisissez le mode du remboursement.",
    referencePaiement: "Ce mode exige sa référence (n° de chèque ou de virement).",
    date: "Saisissez la date du règlement.",
    referentiel: "Les comptes de trésorerie n'ont pas pu être chargés.",
  }),
  enregistrer: "Enregistrer le règlement",
  enCours: "Enregistrement…",
  annuler: "Annuler",
  succes: (numero, etat) => `Règlement de l'avoir ${numero} enregistré — ${etat}.`,
});

/** ux/03 §2.6 — « Annuler l'avoir… » (OP-03-10: number kept, motif required). */
export const M_ANNULATION = Object.freeze({
  titre: (numero) => `Annuler l'avoir ${numero} ?`,
  conserve: "Le numéro est conservé dans le journal, marqué « Annulé ».",
  solde: (ttc) => `Le solde fournisseur est rétabli de ${ttc} DA.`,
  // refonte WP-83 (D-32) — an avoir en instance never entered the solde
  soldeInstance: (ttc) => `L'avoir est en instance : le solde du fournisseur ne change pas ; ses ${ttc} DA quittent les crédits en attente (non déduits).`,
  ristourne: (regle) => `Les encaissements de la ristourne (${regle} DA) sont contre-passés en trésorerie.`,
  ristourneSansEncaissement: "La ristourne n'a pas été encaissée : aucun mouvement de trésorerie.",
  // A-54 (6): a Retour line written before M-11 without a bon de retour — its exit was the avoir's own.
  retours: (u) => `Les ${u} ${pluriel(u, "unité retournée revient", "unités retournées reviennent")} dans ${pluriel(u, "son lot", "leur lot")}.`,
  // WP-31 (OP-03-10, OP-03-21, ux/03 §8 E-AV-3): the exit belongs to the bon de retour — it stays.
  retoursRestent: (u, numeros) =>
    `Les ${u} ${pluriel(u, "unité retournée reste sortie", "unités retournées restent sorties")} du stock (bon de retour ${numeros}) : pour les remettre en stock, annulez ensuite le bon de retour.`,
  succesStock: (numeros) => `Les unités retournées restent sorties du stock : annulez le bon de retour ${numeros} pour les y remettre.`,
  motif: "Motif de l'annulation",
  motifAide: `Au moins ${AVOIR_BORNES.motif_min} caractères — il figure dans le journal et sur le document.`,
  motifCourt: `Saisissez le motif de l'annulation (au moins ${AVOIR_BORNES.motif_min} caractères).`,
  confirmer: "Annuler l'avoir",
  enCours: "Annulation…",
  revenir: "Revenir",
  succes: (numero) => `Avoir ${numero} annulé. Le numéro reste dans le journal.`,
  // the server's own refusal (OP-03-10), said before the request on the grid's Suppr
  refuseRegle: "Seul le propriétaire peut annuler un avoir déjà réglé (même partiellement).",
  // refonte WP-83 (A-104 (8)) — a confirmed credit is in the solde: its cancellation moves it back
  refuseConfirme: "Seul le propriétaire peut annuler un avoir confirmé : son crédit est déduit du solde du fournisseur.",
  // refonte WP-83 × WP-90 (D-35) — the server's sentence (MESSAGES_AVOIR.REGLE_PAR_QUANTITE_ANNULATION), said before the request
  refuseRegleParQuantite: (numero) =>
    `L'avoir produits ${numero} est déjà réglé en partie par quantité : ces règlements (produits livrés, crédits) restent l'historique du document, il ne s'annule plus. Réglez les quantités restantes ou laissez-les en attente.`,
});

/**
 * Refonte WP-83 (CP §3.2, §8.4, §11, Scenario J; D-32, A-80, A-104 (4)-(6)) — « Confirmer l'avoir » of
 * an Écart / Produits avoir en instance: the fournisseur has issued or accepted its avoir.
 */
export const M_CONFIRMATION = Object.freeze({
  titre: "Confirmer l'avoir",
  sousTitre: (numero, ttc) => `Avoir ${numero} en instance · ${ttc} DA`,
  intro: "Le fournisseur a émis ou accepté son avoir : son crédit est déduit du solde du fournisseur, une seule fois.",
  champs: Object.freeze({
    note: "Avoir émis par le fournisseur (plateforme)",
    noteAucune: "Aucun — saisir la référence",
    reference: "Réf. avoir fournisseur",
    date: "Date de l'avoir du fournisseur",
    montant: "Montant confirmé (TTC)",
    observations: "Observations",
  }),
  optionNote: ({ numero, date, type, ttc }) => `${numero} · ${date} · ${type} · ${ttc} DA`,
  aide: Object.freeze({
    note: (numero) => `La référence de votre avoir deviendra ${numero}.`,
    noteListe: "Les avoirs que ce fournisseur connecté a émis sur ses e-factures et qui attendent leur rapprochement.",
    montantEcart: (ttc) => `Jusqu'à ${ttc} DA. Un montant plus faible confirme une partie : le reste devient un nouvel avoir en instance.`,
    montantProduits: "Un avoir produits se confirme pour son montant entier.",
  }),
  effets: Object.freeze({
    titre: "Effet sur le solde fournisseur",
    soldeAvantApres: (avant, apres) => `Solde fournisseur : ${avant} → ${apres}, dès la confirmation`,
    attente: (avant, apres) => `Crédits en attente (non déduits) : ${avant} → ${apres} DA`,
    sansSolde: (m) => `Le solde du fournisseur baisse de ${m} DA dès la confirmation.`,
    reliquat: (reste) => `Confirmation partielle : le reste, ${reste} DA, devient un nouvel avoir en instance (numéro suivant), lié à celui-ci.`,
    uneFois: "La confirmation compte une seule fois : la renvoyer n'a aucun effet de plus.",
    aPayer: (m) => `${m} DA à payer`,
    enFaveur: (m) => `${m} DA en votre faveur`,
    aJour: "à jour",
  }),
  erreurs: Object.freeze({
    reference: "Saisissez la référence de l'avoir du fournisseur, ou choisissez l'avoir qu'il a émis sur la plateforme.",
    referenceLongue: `La référence ne peut pas dépasser ${AVOIR_BORNES.reference_max} caractères.`,
    date: "Saisissez la date de l'avoir du fournisseur.",
    zero: "Saisissez un montant supérieur à 0.",
    montantInvalide: "Saisissez un montant en DA avec au plus deux décimales.",
    depasse: (ttc) => `Le montant confirmé dépasse le montant de l'avoir (${ttc} DA).`,
    produitsPartiel: (ttc) =>
      `Un avoir produits se confirme pour son montant entier (${ttc} DA). Si le fournisseur n'en accorde qu'une partie, annulez cet avoir et saisissez les quantités réellement créditées.`,
    noteInsuffisante: (note, montant, ttc) =>
      `L'avoir fournisseur ${note} (${montant} DA) couvre moins que l'avoir produits (${ttc} DA) : un avoir produits se confirme pour son montant entier.`,
    notes: "Les avoirs émis par le fournisseur n'ont pas pu être chargés : saisissez la référence de son avoir.",
  }),
  confirmer: "Confirmer l'avoir",
  enCours: "Confirmation…",
  annuler: "Annuler",
  succes: (numero, montant) => `Avoir ${numero} confirmé : ${montant} DA déduits du solde du fournisseur.`,
  succesReliquat: (numero, reste) => `Le reste, ${reste} DA, est l'avoir ${numero}, en instance.`,
  /** The notes tab « Rapprocher… » of an avoir en instance: matching it is its confirmation. */
  rapprochementInstance: (numero) => `Votre avoir ${numero} est en instance : le rapprocher le confirme.`,
});

/** ux/03 §2.7 — the connected fournisseurs' credit notes (A-02, OP-03-11). */
export const M_NOTES = Object.freeze({
  grille: "Avoirs émis par vos fournisseurs connectés",
  colonnes: Object.freeze({
    numero: "N°",
    date: "Date",
    fournisseur: "Fournisseur",
    efacture: "E-facture",
    type: "Type",
    ttc: "Total TTC",
    rapprochement: "Rapprochement",
  }),
  rapprocheAvec: (numero) => `Rapproché avec ${numero}`,
  etats: Object.freeze({ A_RAPPROCHER: "À rapprocher", RAPPROCHE: "Rapprochés" }),
  vide: "Aucun avoir émis par vos fournisseurs connectés.",
  videAide: "Les avoirs que vos fournisseurs connectés émettent sur leurs e-factures apparaissent ici, à rapprocher de vos avoirs.",
  videFiltre: "Aucun avoir fournisseur ne correspond à ces critères.",
  erreur: "Les avoirs de vos fournisseurs n'ont pas pu être chargés.",
  tiroir: Object.freeze({
    champs: Object.freeze({
      fournisseur: "Fournisseur",
      date: "Date",
      efacture: "E-facture",
      type: "Type",
      ht: "Total HT",
      ttc: "Total TTC",
      motif: "Motif",
      rapprochement: "Rapprochement",
    }),
    lectureSeule: "Document du fournisseur : lecture seule. Le rapprochement règle votre avoir, il ne modifie pas le sien.",
  }),
  actions: Object.freeze({
    rapprocher: "Rapprocher…",
    creer: "Créer l'avoir correspondant",
    voirAvoir: (numero) => `Voir l'avoir ${numero}`,
  }),
  rapprocher: Object.freeze({
    titre: (numero) => `Rapprocher l'avoir fournisseur ${numero}`,
    intro: (fournisseur, ttc) => `${fournisseur} · ${ttc} DA. Choisissez votre avoir correspondant :`,
    suggestion: Object.freeze({ MEME_FACTURE: "Même facture", MEME_MONTANT: "Même montant" }),
    option: ({ numero, date, type, ttc, reste }) => `${numero} · ${date} · ${type} · ${ttc} DA · reste ${reste} DA`,
    aucun: "Aucun avoir en attente de ce fournisseur. Créez l'avoir correspondant.",
    reference: (numero) => `La référence de votre avoir deviendra ${numero}.`,
    choixLabel: "Votre avoir correspondant",
    confirmer: "Rapprocher",
    annuler: "Annuler",
    enCours: "Rapprochement…",
    choisir: "Choisissez l'avoir à rapprocher.",
    succes: (note, avoir) => `Avoir fournisseur ${note} rapproché avec ${avoir}.`,
    erreur: "Les avoirs à rapprocher n'ont pas pu être chargés.",
  }),
});

/** ux/03 §6.2 — the filtered journal printed A4 landscape. */
export const M_IMPRESSION_AJ = Object.freeze({
  titre: "Journal des avoirs fournisseur",
  periode: (du, au) => (du || au ? `${du ?? "…"} → ${au ?? "…"}` : "Toutes périodes"),
  filtres: (texte) => texte || "Aucun",
  tronque: (n, total) => `Impression limitée aux ${n} premiers avoirs sur ${total} : exportez le journal (CSV) pour la liste complète.`,
  echec: "L'impression du journal n'a pas pu être préparée.",
  vide: "Aucun avoir à imprimer pour ces critères.",
});

/** The type words of a row (ux/03 §1.4 short / compact forms, from the shared twin). */
export const TYPES_JOURNAL = Object.freeze([TYPE_AVOIR.ECART, TYPE_AVOIR.PRODUITS, TYPE_AVOIR.FINANCIER]);
